// @flow

import React from 'react';
import { useHistory } from 'react-router-dom';
import { Link } from '@material-ui/core/';
import {
    Grid,
    Container,
    Typography,
    FormControl,
    TextField,
    CircularProgress,
    Button
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { Help } from '@material-ui/icons';
import Image from '../image';
import Assets from '../../assets';
import useFetch from '../../services/useFetch';
import KeyIcon from '../../../src/assets/images/key_blue.svg';
import { UserContext } from '../../app';
import './style.css';

function SignInForm() {
    const router = useHistory();
    const { handleLoginUser } = React.useContext(UserContext);
    const [state, setState] = React.useState({firmId: '', userName: '', password: ''});
    // const [isToggledToStage, setIsToggledToStage] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [showErrors, setShowErrors] = React.useState(false);
    const [isLoginFailed, setIsLoginFailed] = React.useState(false);
    const [isMaxLoginAttempt, setIsMaxLoginAttempt] = React.useState(false);

    const handleLogin = () => {
        if (state.firmId && state.userName && state.password) {
            setShowErrors(false);
            setIsLoginFailed(false);
            setIsMaxLoginAttempt(false);
            setLoading(true);
            loginAPICall();
        } else {
            setShowErrors(true);
        }
    };

    const handleSSOLogin = () => {
        router.push('/sso');
    };

    const {
        call: loginAPICall,
        data: loginAPIData,
        error: loginAPIError,
    } = useFetch(
        '/V5.0/DeveloperPortal/Authenticate',
        'POST',
        {},
        {},
        {
            firmId: state.firmId,
            userName: state.userName,
            password: state.password
        },
        [],
        { callOnMount: false }
    );

    React.useEffect(() => {
        setLoading(false);
        if (!loginAPIError && loginAPIData?.message === "Authorized") {
            handleLoginUser({ ...loginAPIData, userName: state.userName, domainId: state.firmId }).then(() => {
                router.push('/');
            });
        }
        if (loginAPIError || loginAPIData?.message === "Unauthorized" || loginAPIData?.ErrorCode || loginAPIData?.Message === "An unexpected error occured. Please try again later.") {
            setIsLoginFailed(true);
        }
        if (loginAPIData?.message === "MaxLoginAttemptLimitReachedError") {
            setIsMaxLoginAttempt(true);
        }
    }, [loginAPIData, loginAPIError]);

    const handleKeyDown = (e) => {
        if  (e.key === "Enter") {
            handleLogin();
        }
    };

    React.useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        }
    }, [state]);

    return (
        <Container maxWidth="md" className="signin-form">
            <Grid container>
                <Grid item sm={12} md={12} lg={12} className="img-container">
                    <Image
                        src={Assets.Images.SignIn.Path}
                        alt={Assets.Images.SignIn.Alt}
                        className="signin-img"
                    />
                </Grid>
                <Grid item sm={12} md={12} lg={12} className="title-container">
                    {isLoginFailed ? (
                        <Typography className="error-message">Login failed. Try again.</Typography>
                    ) : null}
                    {isMaxLoginAttempt ? (
                        <Typography className="error-message">Your SurePrep account has been locked since you have exceeded the permissible number of attempts to login. Please try again after 30 mins or contact your firm's system administrator to re-activate your account.</Typography>
                    ) : null}
                    <Typography className="title">Sign In to Developer Portal</Typography>
                    <div className="border" />
                    <Typography className="sign-in-using-your-s">Sign in using your SurePrep credentials:</Typography>
                </Grid>
                {/* <Container maxWidth="sm">
                    <Grid item sm={12} md={12} lg={12} className="toggle-buttons-container">
                        <Typography component="h2" className="options-text">CHOOSE AN ENVIRONMENT:</Typography>
                        <Help scale={20} className="help-icon" />
                    </Grid>
                    <Grid item sm={12} md={12} lg={12}>
                        <ToggleButtonGroup
                          color="#219ace"
                          value={isToggledToStage ? "STAGE" : "PRODUCTION"}
                          exclusive
                          aria-label="toggle to stage or production"
                        >
                            <ToggleButton
                                className={isToggledToStage ? "toggle-button" : "toggle-button-checked"}
                                onClick={() => setIsToggledToStage(false)}
                            >
                                PRODUCTION
                            </ToggleButton>
                            <ToggleButton 
                               className={isToggledToStage ? "toggle-button-checked" : "toggle-button"}
                               onClick={() => setIsToggledToStage(true)}
                            >
                                STAGE
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>
                </Container> */}
                <Grid item sm={12} md={12} lg={12} className="form-container">
                    <Container maxWidth="sm">
                        <Grid container>
                            <Grid item sm={12} md={12} lg={12}>
                                <FormControl onKeyDown={handleKeyDown} onSubmit={handleLogin}>
                                    <TextField
                                        id="domain-id"
                                        label="Firm ID*"
                                        inputProps={{ 'data-testid': 'firmIdInput' }}
                                        placeholder="Firm ID*"
                                        helperText={showErrors && !state.firmId ? "Firm ID cannot be blank" : ""}
                                        error={showErrors && !state.firmId}
                                        fullWidth
                                        onChange={(e) => {
                                            setState({...state, firmId: e.target.value});
                                            setShowErrors(false);
                                        }}
                                    />
                                </FormControl>
                                <FormControl>
                                    <TextField
                                        id="account-id"
                                        label="Username*"
                                        inputProps={{ 'data-testid': 'usernameInput' }}
                                        placeholder="Username*"
                                        helperText={showErrors && !state.userName ? "Username cannot be blank" : ""}
                                        error={showErrors && !state.userName}
                                        fullWidth
                                        onChange={(e) => {
                                            setState({...state, userName: e.target.value});
                                            setShowErrors(false);
                                        }}
                                    />
                                    <TextField
                                        id="signin-password"
                                        label="Password*"
                                        inputProps={{ 'data-testid': 'passwordInput' }}
                                        placeholder="Password*"
                                        helperText={showErrors && !state.password ? "Password cannot be blank" : ""}
                                        error={showErrors && !state.password}
                                        type="password"
                                        fullWidth
                                        onChange={(e) => {
                                            setState({...state, password: e.target.value});
                                            setShowErrors(false);
                                        }}
                                    />
                                    <Button variant="contained" fullWidth className="loginBtn" onClick={handleLogin}>
                                        {loading ? <CircularProgress color="#fff" size={34} /> : 'LOG IN'}
                                    </Button>
                                    <Grid item sm={12} md={12} lg={12} className="SSO-section">              
                                    <Typography component="h3">OR </Typography>
                                    </Grid>
                                    <Button variant="contained" fullWidth className="SSOloginBtn" onClick={handleSSOLogin}>
                                    <img src={KeyIcon} className="keyIco" />
                                        SIGN IN WITH SSO
                                    </Button>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Container>
                </Grid>
            </Grid>
            <Grid
                item
                sm={12}
                md={12}
                lg={12}
                className="link-container"
            >
                <Container maxWidth="lg">
                    <Grid container>
                        <Grid item sm={12} md={12} lg={12}>
                            Having trouble signing in?&nbsp;

                            <Link
                                href="https://production.sureprep.com/"
                            // onClick={e => e.preventDefault()}
                            >
                                Reset your password in SurePrep
                            </Link>
                            &nbsp;or contact&nbsp;
                            {/* </Grid>
                        <Grid item sm={8} md={8} lg={8}> */}
                            <Typography component="span">
                                <Link
                                    href="https://helpcenter.sureprep.com/access/unauthenticated?return_to=https%3A%2F%2Fsureprep.zendesk.com%2Fagent%2Fdashboard"
                                // onClick={e => e.preventDefault()}
                                >
                                    SurePrep Support.
                                </Link>
                            </Typography>
                        </Grid>
                    </Grid>
                </Container>
            </Grid>
        </Container>
    );
}

export default SignInForm;
